import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Stack, // Keep Stack, Grid might be removed if not used elsewhere
} from '@mui/material';
// Removed Grid from imports, will use Box for layout in dialog
import {
  Edit as EditIcon,
  Sync as SyncIcon,
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudSync as CloudSyncIcon,
  Refresh as RefreshIcon, // Add RefreshIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  fetchAllBranches,
  updateBranchSyncStatus,
  updateBranchMenuSyncStatus, 
  updateBranchPosConfig,
  syncBranchWithTalabat,
  syncBranchMenu,
  updateBranchActiveStatus, // Add the new thunk here
  syncAllBranchesDetails, // Import the new thunk
  syncAllBranchItemsThunk, // Import the new thunk for syncAllBranchItems
} from '../store/slices/branchSlice';
import { Branch, BranchFormData, BranchPosConfig, SyncAllResult, SyncAllBranchesResult } from '../types';
import { formatDateTime } from '../utils/helpers';
import PermissionGate from '../components/Common/PermissionGate';
import { usePermission } from '../hooks/usePermissions';

const initialFormData: BranchFormData = {
  name: '',
  isActive: true,
  isSyncEnabled: false,
  isMenuSynced: false, 
  cloudBranchId: '',
  globalEntityId: '',
  chainId: '',
  chainName: '',
  platformKey: '',
  platformPhysicalVendorId: '',
  secondsToAutoAccept: 0,
  transmissionFlow: '',
  localOrderType: 0,
  localRevenueCenterObjNum: 0,
  localTenderMediaObjNum: 0,
  localDiscountTenderMediaObjNum: 0,
  localCashTenderMediaObjNum: 0,
  localCreditCardTenderMediaObjNum: 0,
  autoCloseLocalOrder: false,
  localTableObjectNum: 0,
  localEmployeeObjectNum: 0,
  localMenuLevel: 0,
};

const BranchesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { branches, loading, error, syncingBranches: reduxSyncingBranches } = useAppSelector((state) => state.branches);
  const [open, setOpen] = useState(false);
  const [currentBranch, setCurrentBranch] = useState<Branch | null>(null);
  const [formData, setFormData] = useState<BranchFormData>(initialFormData);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingMenuBranchId, setSyncingMenuBranchId] = useState<string | null>(null);
  const [syncAllResult, setSyncAllResult] = useState<SyncAllResult | null>(null);
  const [syncAllBranchesResult, setSyncAllBranchesResult] = useState<SyncAllBranchesResult | null>(null);
  const [showSyncResultDialog, setShowSyncResultDialog] = useState(false);
  const [showSyncAllBranchesDialog, setShowSyncAllBranchesDialog] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{status?: number; message: string; details?: any} | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  // Permission checks
  const canViewBranches = usePermission('branches:read');
  const canCreateBranches = usePermission('branches:create');
  const canUpdateBranches = usePermission('branches:update');
  const canDeleteBranches = usePermission('branches:delete');
  const canManageBranches = usePermission('branches:manage');
  const canSyncBranches = usePermission('sync:branch_manage');

  // Effect to fetch all branches on component mount
  useEffect(() => {
    if (canViewBranches) {
      dispatch(fetchAllBranches());
    }
  }, [dispatch, canViewBranches]);

  // Helper function to handle errors uniformly
  const handleError = (err: any, defaultMessage: string) => {
    console.error(defaultMessage, err);
    if (typeof err === 'object' && err !== null) {
      setErrorDetails({
        status: err.status,
        message: err.message || defaultMessage,
        details: err.details || err
      });
    } else {
      setErrorDetails({
        message: err || defaultMessage
      });
    }
    setShowErrorDialog(true);
  };

  const handleOpen = (branch: Branch) => {
    setFormData({
      name: branch.name,
      isActive: branch.isActive,
      isSyncEnabled: branch.isSyncEnabled,
      isMenuSynced: branch.isMenuSynced, // Ensure this is populated
      cloudBranchId: branch.cloudBranchId || '',
      globalEntityId: branch.globalEntityId || '',
      chainId: branch.chainId || '',
      chainName: branch.chainName || '',
      platformKey: branch.platformKey || '',
      platformPhysicalVendorId: branch.platformPhysicalVendorId || '',
      secondsToAutoAccept: branch.secondsToAutoAccept || 0,
      transmissionFlow: branch.transmissionFlow || '',
      localOrderType: branch.localOrderType || 0,
      localRevenueCenterObjNum: branch.localRevenueCenterObjNum || 0,
      localTenderMediaObjNum: branch.localTenderMediaObjNum || 0,
      localDiscountTenderMediaObjNum: branch.localDiscountTenderMediaObjNum || 0,
      localCashTenderMediaObjNum: branch.localCashTenderMediaObjNum || 0,
      localCreditCardTenderMediaObjNum: branch.localCreditCardTenderMediaObjNum || 0,
      autoCloseLocalOrder: branch.autoCloseLocalOrder,
      localTableObjectNum: branch.localTableObjectNum || 0,
      localEmployeeObjectNum: branch.localEmployeeObjectNum || 0,
      localMenuLevel: branch.localMenuLevel || 0,
    });
    setCurrentBranch(branch);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentBranch(null);
  };

  const handleSubmit = async () => {
    if (!currentBranch || !currentBranch.id) return;

    try {
      // Update POS Configuration ONLY
      const posConfig: BranchPosConfig = {
        localOrderType: formData.localOrderType,
        localRevenueCenterObjNum: formData.localRevenueCenterObjNum,
        localTenderMediaObjNum: formData.localTenderMediaObjNum,
        localDiscountTenderMediaObjNum: formData.localDiscountTenderMediaObjNum,
        localCashTenderMediaObjNum: formData.localCashTenderMediaObjNum,
        localCreditCardTenderMediaObjNum: formData.localCreditCardTenderMediaObjNum,
        autoCloseLocalOrder: formData.autoCloseLocalOrder, // Added this line
        localTableObjectNum: formData.localTableObjectNum,
        localEmployeeObjectNum: formData.localEmployeeObjectNum,
        localMenuLevel: formData.localMenuLevel,
      };      await dispatch(updateBranchPosConfig({ id: currentBranch.id, config: posConfig })).unwrap();
      
      dispatch(fetchAllBranches()); 
      handleClose();
    } catch (err) {
      handleError(err, 'Failed to save branch POS configuration');
    }
  };
    const handleToggleBranchSync = async (newStatus: boolean) => {
    if (!currentBranch || !currentBranch.id) return;
    const originalStatus = formData.isSyncEnabled;
    setFormData(prev => ({ ...prev, isSyncEnabled: newStatus })); // Optimistic update
    try {
      await dispatch(updateBranchSyncStatus({ id: currentBranch.id, isSyncEnabled: newStatus })).unwrap();
      // Optional: dispatch(fetchAllBranches()); or update specific branch in Redux store
    } catch (err) {
      console.error('Failed to update branch sync status:', err);
      setFormData(prev => ({ ...prev, isSyncEnabled: originalStatus })); // Revert on error
      handleError(err, `Failed to update sync status for branch: ${currentBranch.name}`);
    }
  };
  const handleToggleMenuSync = async (newStatus: boolean) => {
    if (!currentBranch || !currentBranch.id) return;
    const originalStatus = formData.isMenuSynced;
    setFormData(prev => ({ ...prev, isMenuSynced: newStatus })); // Optimistic update
    try {
      await dispatch(updateBranchMenuSyncStatus({ id: currentBranch.id, isMenuSynced: newStatus })).unwrap();
      // Optional: dispatch(fetchAllBranches()); or update specific branch in Redux store
    } catch (err) {
      console.error('Failed to update menu sync status:', err);
      setFormData(prev => ({ ...prev, isMenuSynced: originalStatus })); // Revert on error
      handleError(err, `Failed to update menu sync status for branch: ${currentBranch.name}`);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchAllBranches());
  };
  const handleSyncAllBranches = async () => {
    setSyncingAll(true);
    try {
      const response = await dispatch(syncAllBranchesDetails()).unwrap();
      setSyncAllBranchesResult(response);
      setShowSyncAllBranchesDialog(true);
      // Branches are already updated in the Redux store from the sync response
    } catch (err) {
      handleError(err, 'Failed to sync all branches');
    } finally {
      setSyncingAll(false);
    }
  };  const handleSyncAllItems = async (branchId: string) => {
    setSyncingMenuBranchId(branchId); // Reuse for loading state on the button
    try {
      const response = await dispatch(syncAllBranchItemsThunk(branchId)).unwrap();
      setSyncAllResult(response);
      setShowSyncResultDialog(true);
      // dispatch(fetchAllBranches()); // Or rely on the thunk to return updated branch
    } catch (err) {
      // Find the branch name for better error message
      const branch = branches.find((b: Branch) => b.id === branchId);
      const branchIdentifier = branch ? branch.name : branchId;
      handleError(err, `Failed to sync all items for branch: ${branchIdentifier}`);
    } finally {
      setSyncingMenuBranchId(null);
    }
  };

  const handleLegacySync = async (branch: Branch) => {
    try {
      await dispatch(syncBranchWithTalabat(branch.id)).unwrap();    } catch (error) {
      handleError(error, 'Error syncing branch');
    }
  };

  if (loading && branches.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }  return (
    <Box sx={{ p: 2 }}> {/* MODIFIED HERE: Removed maxHeight and overflowY */}
      <PermissionGate permission="branches:read" fallback={
        <Alert severity="error" sx={{ mt: 2 }}>
          You don't have permission to view branches.
        </Alert>
      }>        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <PermissionGate permissions={["branches:read", "branches:manage"]} fallback={null}>
              <Tooltip title="Refresh Branch List">
                <span> {/* Span for Tooltip on disabled button */}
                  <IconButton
                    onClick={handleRefresh}
                    disabled={loading || syncingAll}
                    color="primary"
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </PermissionGate>
          </Box>
          <PermissionGate permission="sync:branch_manage">
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={handleSyncAllBranches}
              disabled={syncingAll || loading}
            >
              {syncingAll ? <CircularProgress size={24} color="inherit" /> : 'Sync All Branches'} 
            </Button>
          </PermissionGate>
        </Box>

      {loading && branches.length > 0 && <CircularProgress sx={{ display: 'block', margin: 'auto' }} />}      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === 'object' && error !== null ? error.message : error}
        </Alert>
      )}

      <Box 
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
          gap: 3,
          mt: 2
        }}
      >
        {branches.map((branch: Branch) => (
          <Card key={branch.id}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" alignItems="center">
                    <StoreIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h2">
                      {branch.name}
                    </Typography>
                  </Box>                  {/* Action icons in the top-right corner */}
                  <Stack direction="row" spacing={0.5}>                 
                  <PermissionGate permissions={["sync:menu_manage", "sync:branch_manage", "branches:manage"]}>
                      <Tooltip title="Sync All Menu Items, Toppings, etc.">
                        <span> {/* Span for Tooltip on disabled button */}
                          <IconButton
                            size="small"
                            onClick={() => handleSyncAllItems(branch.id)}
                            disabled={syncingMenuBranchId === branch.id || reduxSyncingBranches.includes(branch.id)}
                            color="secondary" 
                          >
                            {syncingMenuBranchId === branch.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CloudSyncIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </PermissionGate>
                    <PermissionGate permissions={["branches:update", "branches:manage"]}>
                      <Tooltip title="Edit Branch Configuration">
                        <IconButton
                          size="small"
                          onClick={() => handleOpen(branch)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </PermissionGate>
                  </Stack>
                </Box>

                {branch.cloudBranchId && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Cloud Branch ID: {branch.cloudBranchId}
                  </Typography>
                )}

                {branch.chainName && (
                  <Typography variant="body2" gutterBottom>
                    Chain: {branch.chainName}
                  </Typography>
                )}                <Box display="flex" gap={1} mt={2} mb={2} flexWrap="wrap">
                  <Chip
                    label={branch.isActive ? 'Active' : 'Inactive'}
                    color={branch.isActive ? 'success' : 'default'}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {branch.isSyncEnabled && <Chip label="Branch Enabled" color="success" size="small" sx={{ ml: 1 }} onClick={(e) => e.stopPropagation()} />}
                  {branch.isMenuSynced && <Chip label="Order Sync Enabled" color="info" size="small" sx={{ ml: 1 }} onClick={(e) => e.stopPropagation()} />}
                  {branch.lastSyncedAt && (                    <Chip
                      label={`Orders Last Sync: ${formatDateTime(branch.lastSyncedAt)}`}
                      color="primary"
                      size="small"
                      variant="outlined"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                   {branch.menuLastSyncedAt && (                    <Chip
                      label={`Menu Last Sync: ${formatDateTime(branch.menuLastSyncedAt)}`}
                      color="secondary"
                      size="small"
                      variant="outlined"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </Box>

                {branch.platformKey && (
                  <Typography variant="caption" display="block" gutterBottom>
                    Platform: {branch.platformKey}
                  </Typography>
                )}
                
            </CardContent>
          </Card>
        ))}
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Branch Configuration</DialogTitle>
        <DialogContent sx={{ overflowY: 'auto' }}> {/* Ensure DialogContent is scrollable */}
          {/* Replaced Grid layout with Box and CSS Grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, 
            gap: 2, 
            pt: 1 
          }}>
            {/* Informational Fields (Read-Only) */}
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
              <TextField autoFocus margin="dense" label="Branch Name" fullWidth variant="outlined" value={formData.name}
                InputProps={{ readOnly: true }} helperText="Branch name (read-only)" />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
              <TextField margin="dense" label="Cloud Branch ID" fullWidth variant="outlined" value={formData.cloudBranchId || ''}
                InputProps={{ readOnly: true }} helperText="Talabat Branch ID (e.g., t123, read-only)" />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
              <TextField margin="dense" label="Platform Key" fullWidth variant="outlined" value={formData.platformKey || ''} InputProps={{ readOnly: true }} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
                <TextField margin="dense" label="Global Entity ID" fullWidth variant="outlined" value={formData.globalEntityId || ''} InputProps={{ readOnly: true }} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
                <TextField margin="dense" label="Chain ID" fullWidth variant="outlined" value={formData.chainId || ''} InputProps={{ readOnly: true }} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
                <TextField margin="dense" label="Chain Name" fullWidth variant="outlined" value={formData.chainName || ''} InputProps={{ readOnly: true }} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
                <TextField margin="dense" label="Platform Physical Vendor ID" fullWidth variant="outlined" value={formData.platformPhysicalVendorId || ''} InputProps={{ readOnly: true }} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
                <TextField margin="dense" label="Seconds to Auto Accept Order" type="number" fullWidth variant="outlined" value={formData.secondsToAutoAccept || 0} InputProps={{ readOnly: true }} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
                <TextField margin="dense" label="Transmission Flow" fullWidth variant="outlined" value={formData.transmissionFlow || ''} InputProps={{ readOnly: true }} />
            </Box>

            {/* Editable Configuration Title */}
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 2', md: 'span 3' }, mt: 2, mb:1 }}>
                 <Typography variant="subtitle1">Order Sync & POS Configuration</Typography>
            </Box>            {/* Editable Switches */}
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    // onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} // Not needed for disabled
                    disabled // Point 1: Make isActive read-only
                  />
                }
                label="Branch Active" 
                sx={{ mt: 1 }}
              />
            </Box>            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
              <PermissionGate permission="sync:branch_control" fallback={
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isSyncEnabled}
                      disabled
                    />
                  }
                  label="Enable Branch (No Permission)" 
                  sx={{ mt: 1 }}
                />
              }>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isSyncEnabled}
                      onChange={(e) => handleToggleBranchSync(e.target.checked)} // Point 2: Use new handler
                    />
                  }
                  label="Enable Branch" // Changed label
                  sx={{ mt: 1 }}
                />
              </PermissionGate>
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
              <PermissionGate permissions={["sync:menu_manage", "branches:manage"]} fallback={
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isMenuSynced}
                      disabled
                    />
                  }
                  label="Enable Order Sync with Talabat (No Permission)" 
                  sx={{ mt: 1 }}
                />
              }>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isMenuSynced} // Point 3: Bind to isMenuSynced
                      onChange={(e) => handleToggleMenuSync(e.target.checked)} // Point 3: Use new handler
                    />
                  }
                  label="Enable Order Sync with Talabat" // Point 3: New switch with this label
                  sx={{ mt: 1 }}
                />
              </PermissionGate>
            </Box>
             
            {/* Editable POS Configuration TextFields */}
            {/* Each takes 1/3 width on md, 1/2 on sm, full on xs */}
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' } }}>
                <TextField margin="dense" label="Local Order Type" type="number" fullWidth variant="outlined" value={formData.localOrderType || 0} onChange={(e) => setFormData({ ...formData, localOrderType: parseInt(e.target.value, 10) || 0 })} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' } }}>
                <TextField margin="dense" label="Local Revenue Center Obj Num" type="number" fullWidth variant="outlined" value={formData.localRevenueCenterObjNum || 0} onChange={(e) => setFormData({ ...formData, localRevenueCenterObjNum: parseInt(e.target.value, 10) || 0 })} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' } }}>
                <TextField margin="dense" label="Local Tender Media Obj Num" type="number" fullWidth variant="outlined" value={formData.localTenderMediaObjNum || 0} onChange={(e) => setFormData({ ...formData, localTenderMediaObjNum: parseInt(e.target.value, 10) || 0 })} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' } }}>
                <TextField margin="dense" label="Local Discount Tender Media Obj Num" type="number" fullWidth variant="outlined" value={formData.localDiscountTenderMediaObjNum || 0} onChange={(e) => setFormData({ ...formData, localDiscountTenderMediaObjNum: parseInt(e.target.value, 10) || 0 })} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' } }}>
                <TextField margin="dense" label="Local Cash Tender Media Obj Num" type="number" fullWidth variant="outlined" value={formData.localCashTenderMediaObjNum || 0} onChange={(e) => setFormData({ ...formData, localCashTenderMediaObjNum: parseInt(e.target.value, 10) || 0 })} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' } }}>
                <TextField margin="dense" label="Local Credit Card Tender Media Obj Num" type="number" fullWidth variant="outlined" value={formData.localCreditCardTenderMediaObjNum || 0} onChange={(e) => setFormData({ ...formData, localCreditCardTenderMediaObjNum: parseInt(e.target.value, 10) || 0 })} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' } }}>
                <TextField margin="dense" label="Local Table Object Num" type="number" fullWidth variant="outlined" value={formData.localTableObjectNum || 0} onChange={(e) => setFormData({ ...formData, localTableObjectNum: parseInt(e.target.value, 10) || 0 })} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' } }}>
                <TextField margin="dense" label="Local Employee Object Num" type="number" fullWidth variant="outlined" value={formData.localEmployeeObjectNum || 0} onChange={(e) => setFormData({ ...formData, localEmployeeObjectNum: parseInt(e.target.value, 10) || 0 })} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' } }}>
                <TextField margin="dense" label="Local Menu Level" type="number" fullWidth variant="outlined" value={formData.localMenuLevel || 0} onChange={(e) => setFormData({ ...formData, localMenuLevel: parseInt(e.target.value, 10) || 0 })} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' } }}>
                <FormControlLabel
                    control={
                    <Switch
                        checked={formData.autoCloseLocalOrder}
                        onChange={(e) => setFormData({ ...formData, autoCloseLocalOrder: e.target.checked })}
                    />
                    }
                    label="Auto Close Local Order"
                    sx={{ mt: 1 }}
                />
            </Box>
          </Box>
        </DialogContent>        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <PermissionGate permissions={["branches:update", "branches:manage"]} fallback={null}>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Update Configuration
            </Button>
          </PermissionGate>
        </DialogActions></Dialog>

      {/* Sync All Items Success Dialog */}
      <Dialog 
        open={showSyncResultDialog} 
        onClose={() => setShowSyncResultDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          Sync All Items - Success
        </DialogTitle>
        <DialogContent>
          {syncAllResult && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                {syncAllResult.message}
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Sync Summary
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      Menu Items
                    </Typography>
                    <Typography variant="h4" sx={{ my: 1 }}>
                      {syncAllResult.menuItems.syncedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Regular Items: {syncAllResult.menuItems.regularItemsCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Modifiers: {syncAllResult.menuItems.modifiersCount}
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="secondary">
                      Toppings
                    </Typography>
                    <Typography variant="h4" sx={{ my: 1 }}>
                      {syncAllResult.toppings.syncedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Regular Items: {syncAllResult.toppings.regularItemsCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Modifiers: {syncAllResult.toppings.modifiersCount}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  Total Synced: {syncAllResult.totalSyncedCount} items
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Branch ID: {syncAllResult.branchId} | Cloud Branch ID: {syncAllResult.cloudBranchId}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSyncResultDialog(false)} variant="contained" color="primary">
            Close
          </Button>        </DialogActions>
      </Dialog>

      {/* Sync All Branches Success Dialog */}
      <Dialog 
        open={showSyncAllBranchesDialog} 
        onClose={() => setShowSyncAllBranchesDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          Sync All Branches - Success
        </DialogTitle>
        <DialogContent>
          {syncAllBranchesResult && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                {syncAllBranchesResult.message}
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Sync Summary
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      Synced Branches
                    </Typography>
                    <Typography variant="h4" sx={{ my: 1 }}>
                      {syncAllBranchesResult.syncedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Successfully synchronized
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="secondary">
                      Active Branches
                    </Typography>
                    <Typography variant="h4" sx={{ my: 1 }}>
                      {syncAllBranchesResult.activeBranchesCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total active branches
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              
              {syncAllBranchesResult.branches && syncAllBranchesResult.branches.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Branch Details
                  </Typography>
                  <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
                    {syncAllBranchesResult.branches.map((branch, index) => (
                      <Card key={branch.id || index} variant="outlined" sx={{ mb: 1 }}>
                        <CardContent sx={{ py: 1.5 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {branch.name}
                              </Typography>
                              {branch.cloudBranchId && (
                                <Typography variant="caption" color="text.secondary">
                                  Cloud ID: {branch.cloudBranchId}
                                </Typography>
                              )}
                            </Box>
                            <Box display="flex" gap={1}>                              <Chip
                                label={branch.isActive ? 'Active' : 'Inactive'}
                                color={branch.isActive ? 'success' : 'default'}
                                size="small"
                                onClick={(e) => e.stopPropagation()}
                              />
                              {branch.isSyncEnabled && (
                                <Chip label="Sync Enabled" color="primary" size="small" onClick={(e) => e.stopPropagation()} />
                              )}
                              {branch.isMenuSynced && (
                                <Chip label="Order Sync" color="info" size="small" onClick={(e) => e.stopPropagation()} />
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSyncAllBranchesDialog(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Details Dialog */}
      <Dialog 
        open={showErrorDialog} 
        onClose={() => setShowErrorDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Error Details
        </DialogTitle>
        <DialogContent>
          {errorDetails && (
            <Box>
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorDetails.message}
              </Alert>
              
              {errorDetails.status && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Status Code:</strong> {errorDetails.status}
                </Typography>
              )}
              
              {errorDetails.details && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Additional Details:
                  </Typography>
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'grey.100', 
                      borderRadius: 1, 
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      overflow: 'auto',
                      maxHeight: '300px'
                    }}
                  >
                    {typeof errorDetails.details === 'string' 
                      ? errorDetails.details 
                      : JSON.stringify(errorDetails.details, null, 2)}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorDialog(false)} variant="contained" color="primary">
            Close
          </Button>        </DialogActions>
      </Dialog>
      </PermissionGate>
    </Box>
  );
};

export default BranchesPage;
